import * as React from "react";
import GliderComponent from "react-glider";
import { useLocation } from "react-router-dom";

import Breadcrumbs from "../Breadcrumbs";
import Downloads from "../Downloads";
import FAQs from "../FAQs";
import FullWidthBanner from "../FullWidthBanner";
import Gallery from "../Gallery";
import LeaderboardOrSquareAdvertisement from "../LeaderboardOrSquareAdvertisement";
import Navigation from "../Navigation";
import Page from "../Page";
import SubNavigation from "../SubNavigation";
import styles from "./style.scss";

const LABELS = {
  OVERVIEW: "Overview",
  MODE_OF_ASSESSMENT: "Mode of Assessment",
  READ_MORE: "Read more",
  DURATION: "Duration",
  PRICE: "Price",
  STATUS: "Status",
  APPLY: "Apply",
  SHOW_INTEREST: "Show Interest",
  START_DATE: "Starting",
};

function EducationDetailPage({ onGetPage }) {
  const { pathname } = useLocation();
  const getPage = React.useCallback(onGetPage, [onGetPage]);
  const [isTrackLoaded, setIsTrackLoaded] = React.useState(false);

  return (
    <Page path={pathname} onGetPage={getPage} scrollToTop>
      {({
        page: {
          title,
          banners,
          gallery,
          subNav,
          downloads,
          faqs,
          overviewHtml,
          moreDetailsHtml,
          modeOfAssessment,
          features,
          duration,
          status,
          price,
          isOpen,
          startDate,
          applyButtonUrl,
          showInterestUrl,
          showBreadcrumbs,
        },
        isReady: isPageReady,
      }) => {
        if (!isPageReady) {
          return null;
        }

        return (
          <section className={styles.section}>
            {banners && (
              <div className={styles.banner}>
                <GliderComponent
                  hasDots={banners.length > 1}
                  hasArrows={false}
                  slidesToShow={1}
                  duration={0.5}
                  skipTrack
                  onLoad={() => setIsTrackLoaded(true)}
                >
                  <div
                    className={`${
                      isTrackLoaded ? "" : styles.track
                    } glider-track`}
                  >
                    {banners.map(
                      (n, index) =>
                        n.images && (
                          <FullWidthBanner
                            key={n.id}
                            lazyload={index > 0}
                            media={n.images}
                            reducedHeight
                          />
                        )
                    )}
                  </div>
                </GliderComponent>
                <div className={styles.textWrapper}>
                  <div
                    className={styles.text}
                    style={{ color: banners[0].color }}
                  >
                    <h1 className={`${styles.title} ${styles.bannerTitle}`}>
                      {title}
                    </h1>
                    {showBreadcrumbs && (
                      <Breadcrumbs url={pathname} color={banners[0].color} />
                    )}
                  </div>
                </div>
              </div>
            )}
            {subNav && (
              <Navigation type={subNav}>
                {({ navigation }) => <SubNavigation navigation={navigation} />}
              </Navigation>
            )}
            <div className={styles.overviewWrapper}>
              <div className={styles.overview}>
                <h2 className={styles.overviewTitle}>{LABELS.OVERVIEW}</h2>
                <ul className={styles.overviewList}>
                  {duration && (
                    <li className={styles.overviewItem}>
                      <span className={styles.overviewItemTitle}>
                        {LABELS.DURATION}
                      </span>
                      <span className={styles.overviewItemValue}>
                        {duration}
                      </span>
                    </li>
                  )}
                  {price && (
                    <li className={styles.overviewItem}>
                      <span className={styles.overviewItemTitle}>
                        {LABELS.PRICE}
                      </span>
                      <span className={styles.overviewItemValue}>{price}</span>
                    </li>
                  )}
                  {status && (
                    <li className={styles.overviewItem}>
                      <span className={styles.overviewItemTitle}>
                        {LABELS.STATUS}
                      </span>
                      <span className={styles.overviewItemValue}>{status}</span>
                    </li>
                  )}
                  {startDate && (
                    <li className={styles.overviewItem}>
                      <span className={styles.overviewItemTitle}>
                        {LABELS.START_DATE}
                      </span>
                      <span className={styles.overviewItemValue}>
                        {startDate}
                      </span>
                    </li>
                  )}
                  <li className={styles.overviewItem}>
                    {isOpen ? (
                      <a
                        href={applyButtonUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={styles.button}
                      >
                        {LABELS.APPLY}
                      </a>
                    ) : (
                      <a
                        href={showInterestUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={styles.button}
                      >
                        {LABELS.SHOW_INTEREST}
                      </a>
                    )}
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.content}>
              {!banners && showBreadcrumbs && <Breadcrumbs url={pathname} />}
              {!banners && <h1 className={styles.title}>{title}</h1>}
              {gallery && Boolean(gallery.length) && (
                <>
                  <Gallery data={gallery} />
                  <div className={styles.thinLine} />
                </>
              )}
              <div
                dangerouslySetInnerHTML={{ __html: overviewHtml }}
                className="richtext"
              />
              {moreDetailsHtml && (
                <details>
                  <summary>
                    <span className={styles.readMore}>{LABELS.READ_MORE}</span>
                  </summary>
                  <div
                    dangerouslySetInnerHTML={{ __html: moreDetailsHtml }}
                    className="richtext"
                  />
                </details>
              )}
              {modeOfAssessment && Boolean(modeOfAssessment.length) && (
                <>
                  <h2 className={styles.subtitle}>
                    {LABELS.MODE_OF_ASSESSMENT}
                  </h2>
                  <ul className={styles.modeOfAssessment}>
                    {modeOfAssessment.map((n) => (
                      <React.Fragment key={n.key}>
                        <li
                          className={styles.modeOfAssessmentItem}
                          style={{ width: `${n.value}%` }}
                        >
                          <span>{n.key}</span>&nbsp;(<span>{n.value}%</span>)
                        </li>
                        <li className={styles.modeOfAssessmentItemMobile}>
                          <div className={styles.modeOfAssessmentLabel}>
                            <span>{n.key}</span>
                            &nbsp;(<span>{n.value}%</span>)
                          </div>
                          <span
                            className={styles.modeOfAssessmentProgressBar}
                            style={{ width: `${n.value}%` }}
                          />
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>
                </>
              )}
              {downloads && Boolean(downloads.length) && (
                <Downloads downloads={downloads} />
              )}
              {faqs && Boolean(faqs.length) && <FAQs faqs={faqs} />}
              <LeaderboardOrSquareAdvertisement uid="education-detail-page-1" />
            </div>
            {features && Boolean(features.length) && (
              <div className={styles.features}>
                <ul className={styles.featuresList}>
                  {features.map((n) => (
                    <li className={styles.featuresItem} key={n.key}>
                      <span className={styles.featuresTitle}>{n.key}</span>
                      <span className={styles.featuresValue}>{n.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      }}
    </Page>
  );
}

export default EducationDetailPage;
